import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, ChevronDown, ChevronUp, Languages, Bookmark, BookmarkCheck, Send, Loader2 } from "lucide-react";

export default function ChatBox({ messages, myId, onSend, translate, saveNote, open, onToggle }) {
  const [text, setText] = useState("");
  const [trans, setTrans] = useState({});
  const [saved, setSaved] = useState({});
  const listRef = useRef(null);

  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const doTranslate = async (m) => {
    setTrans((t) => ({ ...t, [m.id]: { loading: true } }));
    try {
      const r = await translate(m.text);
      setTrans((t) => ({ ...t, [m.id]: { text: r } }));
    } catch (e) {
      setTrans((t) => ({ ...t, [m.id]: { error: true } }));
    }
  };

  const doSave = async (m) => {
    try {
      await saveNote(m, trans[m.id]?.text || "");
      setSaved((s) => ({ ...s, [m.id]: true }));
    } catch (e) { console.error("save note failed", e); toast.error("Could not save note"); }
  };

  const submit = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    onSend(t);
    setText("");
  };

  return (
    <div className="rounded-3xl bg-white/5 border border-white/10 flex flex-col min-h-0 shrink-0" data-testid="chat-box">
      <button
        onClick={onToggle}
        data-testid="chat-toggle"
        className="flex items-center justify-between px-4 py-2.5 text-sm font-heading font-bold text-white/90 hover:bg-white/5 rounded-t-3xl transition-colors"
      >
        <span className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-secondary" /> Chat {messages.length > 0 && <span className="text-xs text-white/40">({messages.length})</span>}</span>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
      </button>

      {open && (
        <>
          <div ref={listRef} className="flex-1 min-h-0 overflow-auto px-3 pb-2 space-y-3 max-h-[38vh]" data-testid="chat-messages">
            {messages.length === 0 && (
              <p className="text-xs text-white/35 text-center py-6">Say hi to your partner 👋</p>
            )}
            {messages.map((m) => {
              const mine = m.senderId === myId;
              const tr = trans[m.id];
              return (
                <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`chat-message-${m.id}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-white/10 text-white"}`}>
                    <p className="text-[10px] opacity-60 mb-0.5">{mine ? "You" : m.senderName}</p>
                    <p className="break-words">{m.text}</p>
                    {tr?.text && (
                      <p className="mt-1.5 pt-1.5 border-t border-white/20 text-xs italic opacity-90" data-testid="chat-translation">
                        {tr.text}
                      </p>
                    )}
                    {tr?.error && (
                      <p className="mt-1.5 pt-1.5 border-t border-white/20 text-xs italic text-amber-300" data-testid="chat-translation-error">
                        Couldn't translate — try again.
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 px-1">
                    <button
                      onClick={() => doTranslate(m)}
                      disabled={tr?.loading}
                      data-testid="chat-translate-btn"
                      className="flex items-center gap-1 text-[11px] font-semibold text-secondary hover:text-secondary/80 transition-colors disabled:opacity-50"
                    >
                      {tr?.loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />} Translate
                    </button>
                    <button
                      onClick={() => doSave(m)}
                      data-testid="chat-save-btn"
                      className="flex items-center gap-1 text-[11px] font-semibold text-white/50 hover:text-white/80 transition-colors"
                    >
                      {saved[m.id] ? <><BookmarkCheck className="w-3 h-3" /> Saved</> : <><Bookmark className="w-3 h-3" /> Save for Later</>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <form onSubmit={submit} className="flex items-center gap-2 p-2.5 border-t border-white/10">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type a message…"
              data-testid="chat-input"
              className="flex-1 bg-white/10 rounded-full px-3.5 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:ring-1 focus:ring-secondary"
            />
            <button type="submit" data-testid="chat-send-btn" className="rounded-full bg-secondary text-secondary-foreground w-9 h-9 flex items-center justify-center shrink-0 hover:opacity-90 transition-opacity">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </>
      )}
    </div>
  );
}
